import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}

const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const extendedObjects = [
    "Apples", "Bananas", "Grapes", "Oranges", "Pencils", "Books", "Cars", "Balls", "Chocolates",
    "Bottles", "Notebooks", "Pens", "Shoes", "Toys", "Mugs", "Cups", "Chairs", "Tables", "Laptops",
    "Phones", "TV Sets", "Bags", "Hats", "Pizzas", "Cookies", "T-Shirts", "Jackets", "Socks", "Blankets",
    "Gloves", "Plates", "Spoons", "Forks", "Knives", "Towels", "Rulers", "Erasers", "Markers", "Stamps",
    "Backpacks", "Umbrellas", "Ice Creams", "Sandwiches", "Waffles", "Donuts", "Mice", "Keyboards",
    "Bottles of Water", "Bicycles", "Trains", "Airplanes", "Tractors", "Blocks", "Stickers", "Cups of Tea",
    "Cups of Coffee", "Flowers", "Trees", "Leaves", "Seeds", "Birds", "Fish", "Animals", "Stars", "Clouds",
    "Sunflowers", "Cars", "Trucks", "Buses", "Motorcycles", "Teddies", "Lollipops", "Light Bulbs", "Paints",
    "Paint Brushes", "Puzzles", "Tickets", "Cupcakes", "Candies", "Fruits", "Vegetables", "Spices", "Grains",
    "Nuts", "Flowers", "Trees", "Flags", "Maps", "Pictures", "Plants", "Parrots", "Snakes", "Rabbits",
    "Fish Tanks", "Aquarium Fish", "Jelly Beans", "Cans", "Bottles of Juice", "Fruits in a Basket",
    "Basket of Eggs"
];

// Helper function to generate random tally marks
const generateTallyMarks = (num: number) => {
    const fullSets = Math.floor(num / 5);
    const remainder = num % 5;
    return `<svg viewBox="0 0 30 30" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
    <!-- Vertical tally lines -->
    <line x1="2" y1="2" x2="2" y2="28" stroke="black" stroke-width="2" />
    <line x1="8" y1="2" x2="8" y2="28" stroke="black" stroke-width="2" />
    <line x1="14" y1="2" x2="14" y2="28" stroke="black" stroke-width="2" />
    <line x1="20" y1="2" x2="20" y2="28" stroke="black" stroke-width="2" />
    <!-- Diagonal line across -->
    <line x1="1" y1="2" x2="25" y2="28" stroke="black" stroke-width="2" />
  </svg>`.repeat(fullSets) + "|".repeat(remainder);
};

// Helper function to generate random counts for objects
const generateObjectData = () => {
    // Select 4 random objects
    const selectedObjects = extendedObjects.sort(() => 0.5 - Math.random()).slice(0, 4);

    return selectedObjects.map((object) => {
        const count = Math.floor(Math.random() * 20) + 1; // Random number between 1 and 20
        return {
            object,
            count,
            tally: generateTallyMarks(count),
        };
    });
};

// Generate dynamic questions
const generateTallyQuestions = (data: any[]) => {
    const totalItems = data.reduce((sum, item) => sum + item.count, 0);
    const mostSoldObject = data.reduce((max, item) => (item.count > max.count ? item : max));
    const leastSoldObject = data.reduce((min, item) => (item.count < min.count ? item : min));
    const total = totalItems;

    return [
        {
            type: "count",
            question: `How many ${data[0]?.object} were counted?`,
            answer: data[0]?.count,
            options: [data[0]?.count - 1, data[0]?.count, data[0]?.count + 2, data[0]?.count - 2]
        },
        {
            type: "count",
            question: `How many ${data[1]?.object} were counted?`,
            answer: data[1]?.count,
            options: [data[1]?.count, data[1]?.count - 1, data[1]?.count - 2, data[1]?.count + 1]
        },
        {
            type: "comparison",
            question: `Which object was counted the most?`,
            answer: mostSoldObject.object,
            options: [data[2].object, data[1].object, data[0].object, data[3].object]
        },
        {
            type: "comparison",
            question: `Which object was counted the least?`,
            answer: leastSoldObject.object,
            options: [data[0].object, data[1].object, data[2].object, data[3].object]
        },
        {
            type: "total",
            question: `How many objects were counted in total?`,
            answer: total,
            options: [total, total + 5, total - 1, total + 1]
        },
        {
            type: "addition",
            question: `What is the total count of ${data[0]?.object} and ${data[1]?.object}?`,
            answer: data[0]?.count + data[1]?.count,
            options: [data[0]?.count + data[1]?.count, data[0]?.count + data[1]?.count + 2, data[0]?.count + data[1]?.count - 1, data[0]?.count + data[1]?.count - 2]
        },
        {
            type: "multiplication",
            question: `What is the total count of ${data[0]?.object} if each is doubled?`,
            answer: data[0]?.count * 2,
            options: [data[0]?.count * 2 - 1, data[0]?.count * 2 + 1, data[0]?.count * 2, data[0]?.count * 2 - 2]
        },
    ];
};

const objects = [
    "🍎 Apples", "🍌 Bananas", "🍇 Grapes", "🍊 Oranges",
    "🚌 Buses", "🚲 Bicycles", "🐕 Dogs", "🐈 Cats",
    "🐦 Birds", "🐟 Fish", "🍓 Strawberries", "🥕 Carrots",
    "🍍 Pineapples", "🌽 Corn", "🍉 Watermelons", "🍋 Lemons",
];


const tableobjects = [
    "Apples",
    "Bananas",
    "Oranges",
    "Grapes",
    "Cars",
    "Trucks",
    "Dogs",
    "Cats",
    "Books",
    "Pens",
    "Chocolates",
    "Bottles",
    "Hats",
    "Shoes",
    "Bags",
    "Pencils",
    "Erasers",
    "Kites",
    "Balls",
    "Lamps",
];

const dynamicColumnNames = [
    "Year 2019",
    "Year 2020",
    "Year 2021",
    "Year 2022",
    "Year 2023",
    "Year 2024",
    "Year 2025",
];

// Generate random table data
const generateTableData = () => {
    const selectedObjects = [];
    const usedIndices = new Set<number>();

    while (selectedObjects.length < 4) {
        const randomIndex = Math.floor(Math.random() * tableobjects.length);
        const column2Value = Math.floor(Math.random() * 50) + 10; // Random value for column 2
        const column3Value = Math.floor(Math.random() * 40) + 20; // Random value for column 3
        const column4Value = Math.floor(Math.random() * 60) + 15; // Random value for column 4

        if (!usedIndices.has(randomIndex)) {
            selectedObjects.push({
                object: tableobjects[randomIndex],
                column2: column2Value,
                column3: column3Value,
                column4: column4Value,
            });
            usedIndices.add(randomIndex);
        }
    }
    return selectedObjects;
};

// Generate random column names
const generateColumnNames = () => {
    const shuffledNames = [...dynamicColumnNames].sort(() => Math.random() - 0.5);
    return shuffledNames.slice(0, 3);
};

// Generate dynamic questions based on the table data
const generateTableQuestions = (data: any[], columnNames: string[]) => {
    const totalColumn2 = data.reduce((sum, item) => sum + item.column2, 0);
    const maxColumn2 = Math.max(...data.map((item) => item.column2));
    const maxColumn2Object = data.find((item) => item.column2 === maxColumn2)?.object;

    return [
        {
            question: `How many ${data[0].object} are sold in ${columnNames[0]}?`,
            answer: data[0].column2,
        },
        {
            question: `What is the total count for ${columnNames[0]}?`,
            answer: totalColumn2,
        },
        {
            question: `Which object has the highest count in ${columnNames[0]}?`,
            answer: maxColumn2Object,
        },
        {
            question: `What is the count of ${data[2].object} in ${columnNames[1]}?`,
            answer: data[2].column3,
        },
        {
            question: `What is the total count for ${columnNames[2]}?`,
            answer: data.reduce((sum, item) => sum + item.column4, 0),
        },
    ];
};

interface Props {
    onComplete?: () => void;
}



const DataHandling: React.FC<Props> = ({ onComplete }) => {
    const [objectData, setObjectData] = useState<any[]>([]);
    const [questions, setQuestions] = useState<any[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [userAnswer, setUserAnswer] = useState<string | number>("");
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const [selectedType, setSelectedType] = useState(0);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [selectedTallyOption, setSelectedTallyOption] = useState<number | null>(null);
    const [data, setData] = useState<any[]>([]);
    const [picquestions, setPicQuestions] = useState<any[]>([]);


    const [tableData, setTableData] = useState<any[]>([]);
    const [tablequestions, setTableQuestions] = useState<any[]>([]);
    const [currentTableQuestionIndex, setCurrentTableQuestionIndex] = useState(0);
    const [columnNames, setColumnNames] = useState<string[]>([]);
    const [selectedTableOption, setSelectedTableOption] = useState<number | null>(null);


    const loadTallyMarksQuestions = async () => {
        const data = generateObjectData();
        setObjectData(data);
        setQuestions(await generateTallyQuestions(data));
        setCurrentQuestionIndex(Math.floor(Math.random() * 6) + 1);
        setUserAnswer("");
    }

    const generatePictographData = () => {
        const selectedObjects = [];
        const usedIndices = new Set<number>();
        const usedQuantities = new Set<number>();

        while (selectedObjects.length < 4) {
            const randomIndex = Math.floor(Math.random() * objects.length);
            const quantity = Math.floor(Math.random() * 9) + 1;

            if (!usedIndices.has(randomIndex) && !usedQuantities.has(quantity)) {
                selectedObjects.push({ object: objects[randomIndex], quantity });
                usedIndices.add(randomIndex);
                usedQuantities.add(quantity);
            }
        }
        return selectedObjects;
    }

    const generateQuestions = (pictographData: any[]) => {
        const total = pictographData.reduce((acc, item) => acc + item.quantity, 0);
        return [
            {
                question: `How many ${pictographData[0].object.split(" ")[1]} are represented?`,
                answer: pictographData[0].quantity,
            },
            {
                question: `How many ${pictographData[1].object.split(" ")[1]} are represented?`,
                answer: pictographData[1].quantity,
            },
            {
                question: `Which object has the highest quantity?`,
                answer: pictographData.reduce((max, item) => item.quantity > max.quantity ? item : max).object,
            },
            {
                question: `What is the total number of objects represented?`,
                answer: total,
            },
        ];
    };

    const loadPictoGraphQuestions = async () => {
        const newData = generatePictographData();
        setData(newData);
        setPicQuestions(generateQuestions(newData));
        setCurrentQuestionIndex(Math.floor(Math.random() * 4));
        setError(false);
        setSelectedOption(null);
    };

    const loadTableQuestions = async () => {
        const columnNames = generateColumnNames();
        const tableData = generateTableData();
        setColumnNames(columnNames);
        setTableData(tableData);
        setTableQuestions(generateTableQuestions(tableData, columnNames));
        setCurrentTableQuestionIndex(Math.floor(Math.random() * 3) + 1);
    }

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    const createNewQuestion = async () => {
        const patternType = getRandomInt(0, 2);
        setSelectedType(patternType);

        const q: Question[] = [];
        if (patternType === 0) {
            await loadTallyMarksQuestions(); // this already sets state
        }
        else if(patternType === 1) {
            await loadPictoGraphQuestions(); // this already sets state
        }
        else {
            await loadTableQuestions(); // this already sets state
        }
    };

    const handleSubmit = (option: any) => {
        const currentQuestion = questions[currentQuestionIndex];
        setSelectedTallyOption(option)
        if (
            option.toString().toLowerCase() === currentQuestion.answer.toString().toLowerCase()
        ) {
            setShowImage(true);
            setTimeout(() => {
                setUserAnswer("");
                createNewQuestion();
                setShowImage(false);
                setError(false);
                setSelectedTallyOption(null)
                onComplete?.()
            }, 5000);
        } else {
            setShowImage(false);
            setError(true);
        }
    };

    const handleAnswerSubmit = (userAnswer: any) => {
        setSelectedOption(userAnswer);
        const correctAnswer = picquestions[currentQuestionIndex].answer;

        if (userAnswer === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                createNewQuestion();
                setSelectedOption(null)
                onComplete?.()
            }, 3000);
        } else {
            setError(true);
        }
    };

    const handleTableAnswerSubmit = (userAnswer: any) => {
        setSelectedTableOption(userAnswer);
        const correctAnswer = tablequestions[currentTableQuestionIndex].answer;
        if (userAnswer === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                createNewQuestion();
                setShowImage(false);
                setError(false);
                setSelectedTableOption(null)
                onComplete?.()
            }, 5000);
        } else {

            setShowImage(false);
            setError(true);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserAnswer(event.target.value);
    };

    useEffect(() => {
        createNewQuestion();

        document.title =
            "Mamas Guidance - Class Three - Mathematics";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 1 IGKO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {selectedType === 0 ? (
                        <>
                            <div className="tally-marks-worksheet">
                                <div className="mb-4">
                                    <div className="text-center">
                                        <h5>The table shows the tally marks for the number of objects. Answer the questions below.</h5>
                                        <div>
                                            <table className="tally-table" style={{ width: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ fontSize: '20px' }}>Object</th>
                                                        <th style={{ fontSize: '20px' }}>Tally Marks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {objectData.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.object}</td>
                                                            <td className="tally-cell" dangerouslySetInnerHTML={{ __html: item.tally }}>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {questions.length > 0 && (
                                    <div className="text-center mb-4">
                                        <h3 className="mb-4">{questions[currentQuestionIndex]?.question}</h3>
                                        <div className="" style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            {questions[currentQuestionIndex]?.options.map((option: any, index: any) => (
                                                <div className={`purpleButton ${selectedTallyOption === option
                                                    ? option === questions[currentQuestionIndex].answer
                                                        ? 'correct'
                                                        : 'incorrect'
                                                    : ''}`}
                                                    key={index}
                                                    onClick={() => handleSubmit(option)}
                                                >
                                                    {option}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : selectedType === 1 ? (
                        <>
                            <div>
                                <div className="mb-4">
                                    <table className="table table-striped table-bordered">
                                        <tbody>
                                            {data.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <strong>{item.object.split(" ")[1]}:</strong>
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: "20px" }}>
                                                            {Array(item.quantity).fill(item.object.split(" ")[0]).join(" ")}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <h3>Question:</h3>
                                    {picquestions.length > 0 && <p>{picquestions[currentQuestionIndex].question}</p>}
                                </div>
                                <div className="" style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    {typeof picquestions[currentQuestionIndex]?.answer === "number" ? (
                                        [picquestions[currentQuestionIndex].answer - 1, picquestions[currentQuestionIndex].answer, picquestions[currentQuestionIndex].answer + 1]
                                            .sort(() => Math.random() - 0.5)
                                            .map((option, index) => (
                                                <div className={`purpleButton ${selectedOption === option
                                                    ? option === picquestions[currentQuestionIndex].answer
                                                        ? 'correct'
                                                        : 'incorrect'
                                                    : ''}`}
                                                    key={index}
                                                    onClick={() => handleAnswerSubmit(option)}
                                                >
                                                    {option}
                                                </div>
                                            ))
                                    ) : (
                                        data.map((item, index) => (
                                            <div className={` ${selectedOption === item.object
                                                ? item.object === picquestions[currentQuestionIndex].answer
                                                    ? 'correct'
                                                    : 'incorrect'
                                                : ''}`}
                                                key={index}
                                                onClick={() => handleAnswerSubmit(item.object)}
                                                style={{ fontSize: "35px" }}
                                            >
                                                {item.object.split(" ")[0]}
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="text-center">
                                    {error && <p className="error-text">❌ Oops! Try again.</p>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <table style={{ width: "100%", border: "1px solid #ccc", marginBottom: "20px" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>Object</th>
                                            {columnNames.map((name, index) => (
                                                <th key={index} style={{ padding: "8px", border: "1px solid #ccc" }}>
                                                    {name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((row, index) => (
                                            <tr key={index}>
                                                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.object}</td>
                                                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column2}</td>
                                                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column3}</td>
                                                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column4}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <h4>Question</h4>
                                <p>{tablequestions[currentTableQuestionIndex]?.question}</p>

                                <div className=" bargraph" style={{ margin: "20px 0", display: "flex", "flexWrap": "wrap", "gap": "30px" }}>
                                    {typeof tablequestions[currentTableQuestionIndex]?.answer === "number" ? (
                                        [tablequestions[currentTableQuestionIndex].answer - 2, tablequestions[currentTableQuestionIndex].answer, tablequestions[currentTableQuestionIndex].answer + 2]
                                            .map((option, idx) => (
                                                <div
                                                    className={`purpleButton ${selectedTableOption === option
                                                            ? option === tablequestions[currentTableQuestionIndex].answer
                                                                ? 'correct'
                                                                : 'incorrect'
                                                            : ''
                                                        }`}
                                                    key={idx} onClick={() => handleTableAnswerSubmit(option)}>
                                                    {option}
                                                </div>
                                            ))
                                    ) : (
                                        tableData.map((row, idx) => (
                                            <div 
                                                className={`purpleButton ${selectedTableOption === row.object
                                                        ? row.object === tablequestions[currentTableQuestionIndex].answer
                                                            ? 'correct'
                                                            : 'incorrect'
                                                        : ''
                                                    }`}
                                                key={idx} onClick={() => handleTableAnswerSubmit(row.object)}>
                                                {row.object}
                                            </div>
                                        ))
                                    )}
                                </div>
                                {error && <p className="error-text">❌ Oops! Try again.</p>}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default DataHandling;
